import {
  LEAD_DROP_POPUP_8_CONSTANTS,
  buildLeadDrop8DynamicDefinitions,
} from "../leadDropPopup/leadDropPopup_8";

/**
 * Lead Drop Form 8 — embeddable (inline) twin of Lead Drop Popup 8.
 *
 * Same IIT phone-first flow + explicit (non-JSON) secondary-field config as popup 8,
 * configured with its OWN row so the inline form and the modal popup hold independent
 * settings. Popup-internal fields (display intervals + delay/exit-intent toggles) have
 * no meaning for an always-rendered inline form, so they are stripped here.
 */
const POPUP_INTERNAL_FIELDS = new Set([
  "popup_display_interval_1",
  "popup_display_interval_2",
  "popup_display_interval_3",
  "enable_delay_form",
  "enable_exit_form",
]);

const stripPopupInternalKeys = (row: Record<string, unknown>) => {
  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    if (!POPUP_INTERNAL_FIELDS.has(key)) next[key] = value;
  }
  return next;
};

/**
 * The fields config is inherited from popup 8 (and popup 1), whose field names
 * map to `lead_drop_popup` DB columns. The `lead_drop_form` table has a
 * DIFFERENT column set (`title`/`sub_text`/`button_text`…), so any inherited
 * popup-column field saved top-level is silently dropped by Sequelize (e.g.
 * `form_title` saved on the popup but not on the form). Only these names exist
 * as real columns on BOTH tables; every other inherited field must be flagged
 * `isJsonField` so Form_4 nests it under `additional_fields`, which the server
 * persists and flattens back to the top level for the widget.
 */
const LEAD_DROP_FORM_SHARED_COLUMNS = new Set([
  "checkbox_description",
  "button_color",
  "button_text_color",
  "terms_conditions_link",
  "privacy_policy_link",
]);

const intoAdditionalFieldsUnlessFormColumn = <
  T extends { fieldName?: unknown; isJsonField?: boolean },
>(
  field: T,
): T =>
  typeof field.fieldName === "string" &&
  !LEAD_DROP_FORM_SHARED_COLUMNS.has(field.fieldName) &&
  !field.isJsonField
    ? { ...field, isJsonField: true }
    : field;

/**
 * The two consent-link fields (terms_conditions_link / privacy_policy_link) are
 * inherited from popup 8's config and kept top-level via LEAD_DROP_FORM_SHARED_COLUMNS
 * above, so they persist as real `lead_drop_form` columns (migration 20260713120000)
 * rather than in additional_fields.
 */
export const LEAD_DROP_FORM_8_CONSTANTS = {
  DEFAULT_ROW: stripPopupInternalKeys(
    LEAD_DROP_POPUP_8_CONSTANTS.DEFAULT_ROW as Record<string, unknown>,
  ),

  LEAD_DROP_FORM_FIELDS_CONFIG:
    LEAD_DROP_POPUP_8_CONSTANTS.LEAD_DROP_POPUP_FIELDS_CONFIG.filter(
      (field) =>
        typeof field.fieldName !== "string" ||
        !POPUP_INTERNAL_FIELDS.has(field.fieldName),
    ).map(intoAdditionalFieldsUnlessFormColumn),

  METADATA_FIELDS: LEAD_DROP_POPUP_8_CONSTANTS.METADATA_FIELDS,
};

export { buildLeadDrop8DynamicDefinitions };
